import { useState } from "react";
import { Dropdown } from "antd";
import EmailSMSConfig from "./EmailSms";
import {
  SettingPageHeader,
  SettingPageWrapper,
  StyledSettingsTabs,
} from "./StyledComponents";
import FileTemplates from "./FileTemplates/FileTemplates";
import NotificationSettings from "./Notifications/NotificationSettings";
import LetterHead from "./LetterHead/LetterHead";
import AIDashboard from "./AIDashboard/AIDashboard";
import AICategorization from "./AICategorization/AICategorization";
import ReferenceLibrary from "./ReferenceLibrary/ReferenceLibrary";
import CostAnalysis from "./CostAnalysis/CostAnalysis";
import GraphExplorer from "./GraphExplorer/GraphExplorer";
import GraphInspector from "./GraphInspector/GraphInspector";
import GeneratedDocuments from "./GeneratedDocuments/GeneratedDocuments";
import RoutingRules from "./RoutingRules/RoutingRules";
import PoaSignatures from "./PoaSignatures/PoaSignatures";
import VaSignUpSettings from "./VaSignUp/VaSignUpSettings";
import PersonalStorageDashboard from "./PersonalStorageDashboard/PersonalStorageDashboard";
// Same dashboard the Fee Notices queue shows behind its "Team
// activity" button — surfaced here too because admins look for
// monitoring dashboards in Settings (Matthew, 2026-08-20).
import TeamActivityView from "../FeeNoticeQueue/components/TeamActivityView";
import useAuth from "../../hooks/useAuth";

// Per-tab capability requirement (2026-08-05 access-control rollout —
// see `users/capabilities.py` in dms_backend). Tabs not listed here are
// open to every admin who reaches this page (the page itself is gated
// admin-only at the route level, see AppRouter's isAdminOnly).
const CAPABILITY_BY_TAB_KEY = Object.freeze({
  "ai-dashboard": "ai.admin_console",
  "ai-categorization": "ai.gold_standard",
  "reference-library": "ai.admin_console",
  "cost-analysis": "ops.console",
  "graph-explorer": "ai.admin_console",
  "graph-inspector": "ai.admin_console",
  "routing-rules": "ops.console",
  "personal-storage-dashboard": "ops.personal_storage_dashboard",
});

const MoreButton = () => (
  <button
    type="button"
    aria-label="More settings tabs"
    style={{
      border: "1px solid #d1d5db",
      background: "#fff",
      borderRadius: 6,
      width: 30,
      height: 30,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      flexDirection: "column",
      gap: 2,
      cursor: "pointer",
    }}
  >
    <span style={{ width: 3, height: 3, borderRadius: 999, background: "#374151" }} />
    <span style={{ width: 3, height: 3, borderRadius: 999, background: "#374151" }} />
    <span style={{ width: 3, height: 3, borderRadius: 999, background: "#374151" }} />
  </button>
);

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState("email");
  const { hasCapability } = useAuth();

  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  const allTabItems = [
    {
      key: "email",
      label: "Email and SMS Configurations",
      children: <EmailSMSConfig />,
    },
    {
      key: "file",
      label: "File templates",
      children: <FileTemplates />,
    },
    {
      key: "generated-documents",
      label: "Generated Documents",
      children: <GeneratedDocuments />,
    },
    {
      key: "notification",
      label: "Notification Settings",
      children: <NotificationSettings />,
    },
    {
      key: "letterhead",
      label: "Letterhead Template",
      children: <LetterHead />,
    },
    {
      key: "ai-dashboard",
      label: "AI Processing Dashboard",
      children: <AIDashboard />,
    },
    {
      key: "ai-categorization",
      label: "AI Categorization",
      children: <AICategorization />,
    },
    {
      key: "reference-library",
      label: "Reference Library",
      children: <ReferenceLibrary />,
    },
    {
      key: "cost-analysis",
      label: "Cost Analysis",
      children: <CostAnalysis />,
    },
    {
      key: "graph-explorer",
      label: "Graph Explorer",
      children: <GraphExplorer />,
    },
    {
      key: "graph-inspector",
      label: "Matter Graph Inspector",
      children: <GraphInspector />,
    },
    {
      key: "routing-rules",
      label: "Routing Rules",
      children: <RoutingRules />,
    },
    {
      key: "poa-signatures",
      label: "Attorney Signatures",
      children: <PoaSignatures />,
    },
    {
      key: "va-sign-up",
      label: "VA Sign-Up Documents",
      children: <VaSignUpSettings />,
    },
    {
      key: "personal-storage-dashboard",
      label: "Personal Storage Usage",
      children: <PersonalStorageDashboard />,
    },
    {
      key: "fee-notice-activity",
      label: "Fee Notice Team Activity",
      // No capability entry: this page is already admin-only at the
      // route level and the backend endpoint enforces admin /
      // super_admin — the same gate the queue's entry point uses.
      children: <TeamActivityView showBackLink={false} />,
    },
  ];

  const tabItems = allTabItems.filter((item) => {
    const required = CAPABILITY_BY_TAB_KEY[item.key];
    return !required || hasCapability(required);
  });

  const primaryTabKeys = [
    "email",
    "file",
    "generated-documents",
    "notification",
    "letterhead",
    "ai-dashboard",
    "ai-categorization",
  ];
  const primaryTabs = tabItems.filter((item) => primaryTabKeys.includes(item.key));
  const overflowTabs = tabItems.filter((item) => !primaryTabKeys.includes(item.key));
  const selectedOverflowTab = overflowTabs.find((item) => item.key === activeTab);
  const displayedTabs = [
    ...primaryTabs,
    ...(selectedOverflowTab ? [selectedOverflowTab] : []),
    {
      key: "settings-more",
      label: (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: overflowTabs.map((item) => ({
              key: item.key,
              label: item.label,
            })),
            onClick: ({ key }) => handleTabChange(key),
          }}
        >
          <span onClick={(event) => event.stopPropagation()}>
            <MoreButton />
          </span>
        </Dropdown>
      ),
      children: null,
    },
  ];

  return (
    <SettingPageWrapper>
      <SettingPageHeader>Settings</SettingPageHeader>
      <StyledSettingsTabs
        activeKey={activeTab}
        onChange={(key) => {
          if (key !== "settings-more") handleTabChange(key);
        }}
        items={displayedTabs}
      />
    </SettingPageWrapper>
  );
};

export default SettingsPage;
